import { tap, map, concatMap } from 'rxjs/operators';
import { ApiResponse } from '@core/models/api-response.model';
import { Observable, Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Withdraw } from '@core/models/withdraw.model';
import { DefaultDataService, HttpUrlGenerator } from '@ngrx/data';
import { Pagination } from '@core/models/pagination.model';
import { Injectable } from '@angular/core';
import { SKIP_ERROR_INTERCEPTOR_HEADER } from '@utils/constants';
import { WithdrawLimit } from '@core/models/withdraw-limit.model';
import { WithdrawLog } from '@core/models/withdraw-log.model';

@Injectable()
export class WithdrawDataServiceV2 extends DefaultDataService<Withdraw> {
  pagination!: Pagination;
  messages$ = new Subject<any[]>();
  messages2$ = new Subject<any[]>();

  constructor(http: HttpClient, httpUrlGenerator: HttpUrlGenerator) {
    super('WithdrawV2', http, httpUrlGenerator, { root: '' });
  }

  add(withdraw: Withdraw): Observable<Withdraw> {
    return this.http.post<ApiResponse>('/withdraw', withdraw).pipe(
      tap(res => this.messages$.next(res.message)),
      concatMap((entity) => this.http.get<ApiResponse>(`/withdraw/${entity.data.rows.id}`).pipe(
        map((row: ApiResponse) => row.data.rows)
      ))
    );
  }

  updateCurrentWithdrawRaw(withdraw: any) {
    return this.http.put<ApiResponse>(`/withdraw/${withdraw.id}`, withdraw);
  }

  update(withdraw: any): Observable<Withdraw> {
    return this.http.put<ApiResponse>(`/withdraw/${withdraw.id}`, withdraw).pipe(
      map(res => res.data.rows)
    );
  }

  updateRemarks(withdraw: any) {
    return this.http.put<ApiResponse>(`/withdraw/remarks/${withdraw.id}`, withdraw).pipe(
      tap(res => this.messages$.next(res.message)),
      concatMap((entity) => this.http.get<ApiResponse>(`/withdraw/v2/${entity.data.rows.id}`).pipe(
        map((row: ApiResponse) => row.data.rows)
      ))
    );
  }

  getById(id: number): Observable<Withdraw> {
    return this.http.get<ApiResponse>(`/withdraw/${id}`).pipe(
      map(res => res.data.rows)
    );
  }

  getWithQuery(pageParam: string): Observable<Withdraw[]> {
    return this.http.get<ApiResponse>(`/withdraw${pageParam}`).pipe(
      tap(res => this.paginationInit(res)),
      map(res => res.data.rows)
    );
  }

  createPayout(data: any, skipErrHandling: boolean = false) {
    const headers: Record<string, string> = {};

    if (skipErrHandling) {
      headers[SKIP_ERROR_INTERCEPTOR_HEADER] = 'true';
    }

    return this.http.post<ApiResponse>('/withdraw/v2/payout', data, { headers }).pipe(
      tap(res => this.messages2$.next(res.message)),
      map(res => ({
        ...res.data,
        message: res.message,
      }))
    );
  }

  createBankPayout(data: any) {
    return this.http.post<ApiResponse>('/withdraw/v2/bankpayout', data).pipe(
      tap(res => this.messages2$.next(res.message)),
      map(res => ({
        ...res.data,
        message: res.message,
      }))
    );
  }

  getPayoutStatus(transactionId: number) {
    return this.http.get<ApiResponse>(`/withdraw/payout/status?bank_transaction_id=${transactionId}`).pipe(
      tap(res => this.messages$.next(res.message)),
      map(res => res.data)
    );
  }

  retryBankPayout(bankTransactionId: number) {
    return this.http.put<ApiResponse>(`/withdraw/retrybankpayout/${bankTransactionId}`, null).pipe(
      tap(res => this.messages2$.next(res.message)),
      map(res => res.data)
    );
  }

  updateBankTransaction(bankTransactionId: number, data: any) {
    return this.http.put<ApiResponse>(`/withdraw/banktransactions/${bankTransactionId}`, data).pipe(
      tap(res => this.messages2$.next(res.message)),
      map(res => res.data)
    );
  }

  getCallbackStatus(transactionId: number) {
    return this.http.get<ApiResponse>(`/withdraw/callbackstatus/${transactionId}`).pipe(
      map(res => res.data)
    );
  }

  getLimit(pageParam: string): Observable<WithdrawLimit> {
    return this.http.get<ApiResponse>(`/withdraw/limit${pageParam}`).pipe(
      map(res => res.data.rows)
    );
  }

  private paginationInit(res: any) {
    if (res) {
      this.pagination = res.data.paginations;
    }
  }
}
